import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider, useLang } from "@/context/LangContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/components/Login";
import MainApp from "@/components/MainApp";
import PaymentReturn from "@/components/PaymentReturn";
import { Loader2 } from "lucide-react";

function AuthCallback() {
  const { googleSession } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        if (sid) await googleSession(sid);
      } catch (e) {
        // ignore
      } finally {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      }
    })();
  }, [googleSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
}

function Gate() {
  const { user, loading } = useAuth();
  const [, force] = useState(0);
  useEffect(() => { force((n) => n + 1); }, [user]);
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  return <MainApp />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/payment/success" element={<PaymentReturn success />} />
      <Route path="/payment/cancel" element={<PaymentReturn success={false} />} />
      <Route path="*" element={<Gate />} />
    </Routes>
  );
}

function Shell() {
  useLang();
  return (
    <div className="App dark">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-center" theme="dark" richColors />
    </div>
  );
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </LangProvider>
  );
}
